import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Sparkles } from "lucide-react";
import { BOOKING_URL } from "@/lib/api";
import { Container, MaskedLines, GlowOrb, goldBtn, ghostBtn } from "@/components/kit";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section
      ref={ref}
      data-testid="home-hero"
      className="relative overflow-hidden pt-36 pb-24 md:pt-44 md:pb-28"
    >
      <motion.div style={{ y: glowY }} className="absolute inset-0 -z-10">
        <GlowOrb className="left-[-8%] top-[6%] h-[520px] w-[520px] opacity-90" />
        <GlowOrb className="right-[2%] top-[30%] h-[420px] w-[420px] opacity-70" />
      </motion.div>
      <div className="absolute inset-0 -z-10 grain opacity-[0.035]" />

      <Container className="grid items-center gap-16 lg:grid-cols-[1.05fr_0.95fr]">
        {/* Left */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-gold/25 bg-gold/5 px-4 py-2"
          >
            <Sparkles className="h-3.5 w-3.5 text-gold" />
            <span className="font-mono-jb text-[11px] md:text-xs tracking-[0.2em] uppercase text-white/70">
              Appointment Lab · Pay-For-Performance
            </span>
          </motion.div>

          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter leading-[0.98] text-white">
            <MaskedLines
              lines={["Precision-Engineered", "B2B Meetings That"]}
              delay={0.15}
            />
            <MaskedLines
              lines={["Actually Convert"]}
              lineClassName="text-gold text-glow-gold"
              delay={0.15 + 2 * 0.11}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-white/65"
          >
            We run deep ICP research and multi-channel outbound to book qualified,
            ICP-matched meetings. You pay only when they're{" "}
            <span className="text-white">qualified, attended, and aligned</span> to your
            parameters — never for random calendar noise.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-cta"
              className={goldBtn}
            >
              Book A Discovery Call
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <Link to="/services" data-testid="hero-launch-cta" className={ghostBtn}>
              Launch Your Outbound Campaign
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
            className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 font-mono-jb text-xs text-white/45"
          >
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-gold" /> 92% ICP accuracy
            </span>
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-gold" /> $0 for unqualified
            </span>
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-gold" /> B2B tech & SaaS
            </span>
          </motion.div>
        </div>

        {/* Right visual */}
        <motion.div style={{ y: visualY }} className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[1.8rem] border border-white/10 overflow-hidden shadow-[0_30px_120px_-30px_rgba(0,0,0,0.8)]"
          >
            <img
              src="https://static.prod-images.emergentagent.com/jobs/ec31a5d1-9734-408d-b99e-f684b9731cbb/images/30bfeb824f0ede9e07b61f3f39e39db3d27ff4c31f045990457d8db002a16023.jpeg"
              alt="Calendar with booked qualified meetings driving rising revenue"
              className="h-[420px] md:h-[540px] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/20 to-transparent" />
            <div className="absolute inset-0 ring-1 ring-inset ring-gold/10 rounded-[1.8rem]" />
          </motion.div>

          {/* Live booked badge */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-live-badge"
            className="absolute -left-4 md:-left-8 top-10 flex items-center gap-3 rounded-2xl border border-white/10 bg-navy-800/90 backdrop-blur-md px-4 py-3 shadow-xl"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 animate-pulse-ring" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
            </span>
            <div>
              <p className="font-mono-jb text-[10px] uppercase tracking-widest text-white/45">
                Live
              </p>
              <p className="text-sm font-semibold text-white">Qualified meeting booked</p>
            </div>
          </motion.div>

          {/* ICP match floating metric */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-icp-metric"
            className="absolute -right-3 md:-right-6 bottom-8 rounded-2xl border border-gold/25 bg-navy-800/90 backdrop-blur-md px-5 py-4 shadow-xl animate-float"
          >
            <p className="font-mono-jb text-[10px] uppercase tracking-widest text-white/45">
              ICP Match Score
            </p>
            <p className="font-mono-jb text-3xl font-semibold text-gold">98.4%</p>
            <div className="mt-2 h-1.5 w-32 overflow-hidden rounded-full bg-white/10">
              <div className="h-full w-[98%] rounded-full bg-gold" />
            </div>
          </motion.div>
        </motion.div>
      </Container>
    </section>
  );
};
